/**
 * Hujjat preview'i (2026-10-18, foydalanuvchi so'rovi): Apple Preview kabi -- rasm sahnaga sig'dirilgan, kattalashtirish (25% qadam),
 * burish, oldingi/keyingi hujjat (strelkalar); o'ngda hujjat ma'lumoti, OCR o'qigan maydonlar va rasmdagi matn qatorlari.
 * "Jonli matn" (iPhone galereyasidagi matn tanlash kabi): OCR qaytargan qatorlar (`ocr.lines`, 0-1000 shkala) rasm ustida shaffof
 * qutilar -- bosilsa matn buferga nusxalanadi; ro'yxatdagi qator va o'qilgan maydon ham. PDF brauzer ko'ruvchisida (matn u yerda
 * tanlanadi). Yuklab olish serverdagi nom bilan: driver ismi, tur, shtat, muddat (`documents.services.download_name`).
 * Geometriya va qo'shnilar: features/documents/preview.ts. Oddiy `Modal` emas: sahna deyarli butun oynani oladi.
 */
import * as Dialog from "@radix-ui/react-dialog";
import { useCallback, useEffect, useState, type KeyboardEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Archive, CalendarDays, Check, ChevronLeft, ChevronRight, Copy, Download, Maximize2, Minus, Plus, RotateCw, ScanText, X, type LucideIcon } from "lucide-react";
import { cn, formatDate, formatDateTime } from "@/lib/utils";
import { boxStyle, fitLayout, hasLiveText, neighbors, rotateNext, stepZoom, type Rotation, type Size } from "@/features/documents/preview";
import { formatBytes, type Translate } from "@/features/documents/status";
import type { DriverDocument, OcrLine } from "@/features/documents/types";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/misc";
import { toast } from "@/components/ui/toast";
import { StatusDot } from "./DocumentStatusDot";

interface Props {
  doc: DriverDocument | null;
  /** Oldingi/keyingi uchun ro'yxat: fayli bor joriy hujjatlar, keyin eski versiyalar */
  docs: DriverDocument[];
  label: (d: DriverDocument) => string;
  onSelect: (d: DriverDocument) => void;
  onClose: () => void;
  onEditDates: (d: DriverDocument) => void;
  onArchive: (d: DriverDocument) => void;
}

const FIELD_ORDER = ["full_name", "first_name", "last_name", "date_of_birth", "cdl_number", "cdl_class", "cdl_state", "issued_at", "expires_at", "endorsements", "restrictions", "examiner_name", "registry_number"];
const fieldLabel = (t: Translate, key: string) => t(`docFields.${key}`, { defaultValue: t(`fields.${key}`, { defaultValue: key }) });

export function DocumentPreview({ doc, docs, label, onSelect, onClose, onEditDates, onArchive }: Props) {
  const { t } = useTranslation("documents");
  const { t: tc } = useTranslation();
  const [zoom, setZoom] = useState(1);
  const [rot, setRot] = useState<Rotation>(0);
  const [natural, setNatural] = useState<Size | null>(null);
  const [stage, setStage] = useState<Size>({ w: 0, h: 0 });
  const [showText, setShowText] = useState(true);
  const [copied, setCopied] = useState<string | null>(null);
  // Sahna elementi state'da: Radix portal bir tik keyin chiziladi, oddiy ref bilan birinchi effekt uni ko'rmay qoladi
  const [stageEl, setStageEl] = useState<HTMLDivElement | null>(null);
  const docId = doc?.id;

  useEffect(() => {
    setZoom(1);
    setRot(0);
    setNatural(null);
  }, [docId]);

  // Sahna o'lchami: ochilganda va oyna o'zgarganda (ResizeObserver); 16px ichki chekka
  useEffect(() => {
    const el = stageEl;
    if (!el) return;
    const measure = () => setStage({ w: Math.max(0, el.clientWidth - 32), h: Math.max(0, el.clientHeight - 32) });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [stageEl]);

  const copy = useCallback(
    async (text: string, key: string) => {
      const done = () => {
        toast.success(t("preview.copied"), text.length > 80 ? `${text.slice(0, 80)}…` : text);
        setCopied(key);
        window.setTimeout(() => setCopied((k) => (k === key ? null : k)), 1200);
      };
      try {
        await navigator.clipboard.writeText(text);
        done();
      } catch {
        if (fallbackCopy(text)) done();
        else toast.error(t("preview.copyFailed"));
      }
    },
    [t],
  );

  if (!doc) return null;

  const isPdf = doc.content_type === "application/pdf";
  const url = doc.file_url ?? "";
  const nav = neighbors(docs, doc.id);
  const lines: OcrLine[] = hasLiveText(doc) ? (doc.ocr?.lines ?? []) : [];
  const layout = natural ? fitLayout(natural, stage, rot, zoom) : null;
  const meta = [
    doc.original_name,
    formatBytes(doc.size),
    doc.pages > 1 ? t("pages", { count: doc.pages }) : "",
    nav.total > 1 && nav.index >= 0 ? t("preview.of", { n: nav.index + 1, total: nav.total }) : "",
    doc.is_archived ? t("viewer.archivedNote", { date: formatDate(doc.archived_at) }) : "",
  ].filter(Boolean);

  function onKey(e: KeyboardEvent<HTMLDivElement>) {
    const el = e.target as HTMLElement;
    if (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable) return;
    if (e.key === "ArrowLeft" && nav.prev) {
      e.preventDefault();
      onSelect(nav.prev);
    } else if (e.key === "ArrowRight" && nav.next) {
      e.preventDefault();
      onSelect(nav.next);
    } else if (!isPdf && (e.key === "+" || e.key === "=")) {
      e.preventDefault();
      setZoom((z) => stepZoom(z, 1));
    } else if (!isPdf && e.key === "-") {
      e.preventDefault();
      setZoom((z) => stepZoom(z, -1));
    } else if (!isPdf && e.key === "0") {
      e.preventDefault();
      setZoom(1);
    } else if (!isPdf && (e.key === "r" || e.key === "R")) {
      e.preventDefault();
      setRot(rotateNext);
    }
  }

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fade-in fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px] dark:bg-black/70" />
        <Dialog.Content
          onKeyDown={onKey}
          className="pop-in-center fixed top-1/2 left-1/2 z-50 flex h-[calc(100vh-32px)] w-[calc(100%-32px)] max-w-[1320px] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-card border border-border/60 bg-surface shadow-menu focus:outline-none"
        >
          <header className="flex items-center gap-3 border-b border-line px-5 py-3">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="truncate text-[16px] leading-snug font-semibold tracking-[-0.01em]">
                {label(doc)}
                {doc.state ? ` · ${doc.state}` : ""}
              </Dialog.Title>
              <Dialog.Description className="mt-0.5 truncate text-[12px] text-muted">{meta.join(" · ")}</Dialog.Description>
            </div>
            {!isPdf && (
              <div className="flex items-center gap-0.5 rounded-full bg-fill p-0.5">
                <Tool icon={Minus} label={t("preview.zoomOut")} onClick={() => setZoom((z) => stepZoom(z, -1))} />
                <button type="button" onClick={() => setZoom(1)} title={t("preview.fit")} className="min-w-[48px] rounded-full px-1.5 text-[12px] font-medium tabular-nums hover:bg-surface">
                  {Math.round(zoom * 100)}%
                </button>
                <Tool icon={Plus} label={t("preview.zoomIn")} onClick={() => setZoom((z) => stepZoom(z, 1))} />
                <Tool icon={Maximize2} label={t("preview.fit")} onClick={() => setZoom(1)} />
                <Tool icon={RotateCw} label={t("preview.rotate")} onClick={() => setRot(rotateNext)} />
                {lines.length > 0 && <Tool icon={ScanText} label={t("preview.textLayer")} active={showText} onClick={() => setShowText((v) => !v)} />}
              </div>
            )}
            <Dialog.Close className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-muted transition-colors hover:bg-fill hover:text-ink" aria-label={tc("actions.close")}>
              <X size={16} />
            </Dialog.Close>
          </header>

          <div className="flex min-h-0 flex-1">
            <div className="relative min-w-0 flex-1">
              <div ref={setStageEl} className="absolute inset-0 overflow-auto bg-fill">
                {isPdf ? (
                  <iframe title={label(doc)} src={url} className="h-full w-full" />
                ) : (
                  <div className="inline-flex min-h-full min-w-full items-center justify-center p-4 align-top">
                    <div className="relative shrink-0" style={layout ? { width: layout.box.w, height: layout.box.h } : undefined}>
                      <div
                        className={cn(layout ? "absolute" : "static")}
                        style={layout ? { width: layout.img.w, height: layout.img.h, left: layout.offset.x, top: layout.offset.y, transform: `rotate(${rot}deg)` } : undefined}
                      >
                        <img
                          src={url}
                          alt={label(doc)}
                          draggable={false}
                          onLoad={(e) => setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
                          className={cn("block rounded-[6px] shadow-card select-none", layout ? "h-full w-full" : "max-h-[60vh] opacity-0")}
                        />
                        {layout &&
                          showText &&
                          lines.map((ln, i) => (
                            <button
                              key={i}
                              type="button"
                              title={ln.text}
                              aria-label={t("preview.copyLine", { text: ln.text })}
                              onClick={() => void copy(ln.text, `box-${i}`)}
                              style={boxStyle(ln.box)}
                              className={cn(
                                "absolute rounded-[3px] border transition-colors focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:outline-none",
                                copied === `box-${i}` ? "border-success bg-success/25" : "border-accent/50 bg-accent/10 hover:border-accent hover:bg-accent/25",
                              )}
                            />
                          ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {!isPdf && !natural && (
                <div className="pointer-events-none absolute inset-0 grid place-items-center">
                  <Spinner className="h-6 w-6" />
                </div>
              )}
              {nav.prev && <NavButton side="left" label={t("preview.prev")} onClick={() => nav.prev && onSelect(nav.prev)} />}
              {nav.next && <NavButton side="right" label={t("preview.next")} onClick={() => nav.next && onSelect(nav.next)} />}
            </div>

            <aside className="flex w-[300px] shrink-0 flex-col border-l border-line">
              <div className="min-h-0 flex-1 space-y-4 overflow-auto p-4 text-[13px]">
                <dl className="space-y-2.5">
                  <Fact label={t("viewer.status")}>
                    <span className="inline-flex items-center gap-2">
                      <StatusDot status={doc.status} size="sm" /> {t(`statusName.${doc.status}`)}
                    </span>
                  </Fact>
                  <Fact label={t("viewer.uploaded")}>
                    {formatDateTime(doc.created_at)}
                    {doc.uploaded_by ? ` · ${doc.uploaded_by.display_name}` : ""}
                  </Fact>
                  {doc.state && <Fact label={t("viewer.state")}>{doc.state}</Fact>}
                  <Fact label={t("dates.issued")}>{formatDate(doc.issued_at)}</Fact>
                  <Fact label={t("dates.expires")}>{formatDate(doc.expires_at)}</Fact>
                </dl>
                <ReadPanel doc={doc} lines={lines} isPdf={isPdf} copied={copied} onCopy={copy} />
              </div>
              <div className="flex flex-wrap gap-2 border-t border-line p-3">
                <Button size="sm" onClick={() => window.open(`${url}?download=1`, "_blank", "noopener")}>
                  <Download size={12} /> {t("actions.download")}
                </Button>
                {!doc.is_archived && (
                  <Button size="sm" onClick={() => onEditDates(doc)}>
                    <CalendarDays size={12} /> {t("actions.editDates")}
                  </Button>
                )}
                {!doc.is_archived && (
                  <Button size="sm" variant="danger" onClick={() => onArchive(doc)}>
                    <Archive size={12} /> {t("actions.archive")}
                  </Button>
                )}
              </div>
            </aside>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** O'ngdagi "o'qilgan" bo'lim: OCR maydonlari va rasmdagi matn qatorlari, har biri bosilsa nusxalanadi */
function ReadPanel({ doc, lines, isPdf, copied, onCopy }: { doc: DriverDocument; lines: OcrLine[]; isPdf: boolean; copied: string | null; onCopy: (text: string, key: string) => Promise<void> }) {
  const { t } = useTranslation("documents");
  if (isPdf) return <p className="text-[12px] text-muted">{t("preview.pdfHint")}</p>;
  if (doc.ocr_status === "pending") {
    return (
      <p className="inline-flex items-center gap-2 text-[12px] text-muted">
        <Spinner className="h-3.5 w-3.5" /> {t("preview.reading")}
      </p>
    );
  }
  if (doc.ocr_status === "off") return null;
  if (!doc.ocr || doc.ocr_status !== "done") return <p className="text-[12px] text-muted">{t("preview.noText")}</p>;
  const order = (k: string) => (FIELD_ORDER.includes(k) ? FIELD_ORDER.indexOf(k) : FIELD_ORDER.length);
  const fields = Object.entries(doc.ocr.fields ?? {})
    .filter(([, v]) => v !== "" && v != null && !(Array.isArray(v) && v.length === 0))
    .sort((a, b) => order(a[0]) - order(b[0]));
  const CopyMark = ({ active }: { active: boolean }) =>
    active ? <Check size={13} className="shrink-0 text-success" aria-hidden /> : <Copy size={13} className="shrink-0 text-faint opacity-0 transition-opacity group-hover:opacity-100" aria-hidden />;
  return (
    <>
      {fields.length > 0 && (
        <section>
          <h4 className="text-[11px] font-semibold tracking-[0.06em] text-muted uppercase">{t("preview.readFields")}</h4>
          <ul className="mt-1 divide-y divide-line">
            {fields.map(([k, v]) => {
              const text = Array.isArray(v) ? v.map(String).join(", ") : String(v);
              const key = `field-${k}`;
              return (
                <li key={k}>
                  <button type="button" onClick={() => void onCopy(text, key)} className="group flex w-full items-center gap-2 py-1.5 text-left">
                    <span className="min-w-0 flex-1">
                      <span className="block text-[11px] text-muted">{fieldLabel(t, k)}</span>
                      <span className="block truncate font-medium tabular-nums">{text}</span>
                    </span>
                    <CopyMark active={copied === key} />
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      )}
      <section>
        <h4 className="text-[11px] font-semibold tracking-[0.06em] text-muted uppercase">{t("preview.lines")}</h4>
        {lines.length === 0 ? (
          <p className="mt-1 text-[12px] text-muted">{t("preview.noText")}</p>
        ) : (
          <>
            <p className="mt-1 text-[12px] text-muted">{t("preview.copyHint")}</p>
            <ul className="mt-1.5 space-y-0.5">
              {lines.map((ln, i) => {
                const key = `line-${i}`;
                return (
                  <li key={i}>
                    <button
                      type="button"
                      onClick={() => void onCopy(ln.text, key)}
                      className={cn("group flex w-full items-start gap-2 rounded-[8px] px-2 py-1 text-left text-[12.5px] transition-colors hover:bg-fill", copied === key && "bg-success-soft")}
                    >
                      <span className="min-w-0 flex-1 break-words">{ln.text}</span>
                      <CopyMark active={copied === key} />
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </section>
    </>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-[11px] font-semibold tracking-[0.06em] text-muted uppercase">{label}</dt>
      <dd className="mt-0.5 font-medium">{children}</dd>
    </div>
  );
}

function Tool({ icon: Icon, label, onClick, active }: { icon: LucideIcon; label: string; onClick: () => void; active?: boolean }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      onClick={onClick}
      className={cn("grid h-7 w-7 place-items-center rounded-full text-ink-soft transition-colors hover:bg-surface hover:text-ink", active && "bg-surface text-ink shadow-card")}
    >
      <Icon size={15} strokeWidth={1.75} aria-hidden />
    </button>
  );
}

function NavButton({ side, label, onClick }: { side: "left" | "right"; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className={cn(
        "absolute top-1/2 z-10 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full border border-border/60 bg-surface/90 text-ink shadow-card backdrop-blur transition-colors hover:bg-surface",
        side === "left" ? "left-3" : "right-3",
      )}
    >
      {side === "left" ? <ChevronLeft size={18} aria-hidden /> : <ChevronRight size={18} aria-hidden />}
    </button>
  );
}

/** `navigator.clipboard` bo'lmaganda (eski brauzer, http): yashirin maydon orqali */
function fallbackCopy(text: string): boolean {
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "");
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  let ok = false;
  try {
    ok = document.execCommand("copy");
  } catch {
    ok = false;
  }
  ta.remove();
  return ok;
}
